import { type ReactNode } from 'react';

import { Image } from '@bonusly/design-system';

import bonuslyMark from '../../../assets/images/bonusly-logo.mark.svg';
import type { BizyPeopleSource } from '../../../lib/bizy/types';
import { BizyLogoChip } from '../../bizy/bizy-logo-chip';
import { GithubLogo } from '../../github/github-logo';
import { GranolaLogo } from '../../granola/granola-logo';
import { LinearLogo } from '../../linear/linear-logo';
import { MsTeamsLogo } from '../../ms-teams/ms-teams-logo';
import { SlackLogo } from '../../slack/slack-logo';

// Small connector-source chip for People Browser moments (BIZY-637). Mirrors the
// source map used by bonus-card `BizyAttribution` so recognition surfaces share
// a consistent look. `linear` isn't a member-facing source today, but the map
// covers every BizyPeopleSource plus that one for reuse.

const SOURCE_ICONS: Record<string, { renderIcon: (size: string) => ReactNode; bg: string }> = {
	slack: { renderIcon: (size) => <SlackLogo width={size} height={size} />, bg: 'gray.25' },
	github: { renderIcon: (size) => <GithubLogo width={size} height={size} />, bg: 'gray.25' },
	linear: { renderIcon: (size) => <LinearLogo width={size} height={size} />, bg: 'purple.25' },
	granola: { renderIcon: (size) => <GranolaLogo width={size} height={size} />, bg: 'gray.25' },
	ms_teams: { renderIcon: (size) => <MsTeamsLogo width={size} height={size} />, bg: 'purple.25' },
	bonusly: { renderIcon: (size) => <Image src={bonuslyMark} alt="" boxSize={size} />, bg: 'green.25' },
};

export interface BizySourceIconProps {
	source: BizyPeopleSource | null | undefined;
	size?: string;
}

/** Renders the brand chip for a moment's connector source, or nothing if unknown/absent. */
export function BizySourceIcon({ source, size = '24px' }: BizySourceIconProps) {
	const chip = source ? SOURCE_ICONS[source] : undefined;
	if (!chip) return null;

	// The design's chips fill 75% with the logo (24px chip → 18px glyph).
	const glyphSize = `${Math.round(parseInt(size, 10) * 0.75)}px`;

	return (
		<BizyLogoChip
			boxSize={size}
			bg={chip.bg}
			// Chip bg is always light; pin the icon color so currentColor logos
			// (GitHub, Linear, Granola) stay dark and don't vanish.
			color="gray.900"
		>
			{chip.renderIcon(glyphSize)}
		</BizyLogoChip>
	);
}
